<template>
  <Drawer
    v-model:visible="isVisible"
    :modal="false"
    position="right"
    :style="{ width: '60rem' }"
    :pt="{
      content: { class: 'p-0 flex flex-col !overflow-hidden' }
    }"
  >
    <template #header>
      <div class="flex items-center gap-2">
        <i class="pi pi-chart-bar text-lg" />
        <span class="font-semibold">{{ flowName }}</span>
        <Tag
          v-if="totalRecords > 0"
          :value="totalAllRecords > totalRecords
            ? `${totalRecords.toLocaleString()} engaged (${totalAllRecords.toLocaleString()} total)`
            : `${totalRecords.toLocaleString()} records`"
          severity="secondary"
          rounded
        />
      </div>
    </template>

    <div class="flex flex-1 flex-col overflow-hidden">
      <!-- Filters Bar -->
      <div class="flex flex-wrap items-center gap-3 border-b border-gray-200 px-4 py-3 dark:border-gray-700 [&_input]:!h-8 [&_button]:!h-8">
        <!-- Date Range -->
        <div class="flex items-center gap-2">
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400">From</label>
          <DatePicker
            v-model="startDate"
            date-format="M dd, yy"
            :max-date="endDate"
            show-icon
            icon-display="input"
            input-class="text-xs w-[8rem]"
            :pt="{ pcInput: { root: { class: 'text-xs !py-0' } } }"
          />
          <label class="text-xs font-medium text-gray-500 dark:text-gray-400">To</label>
          <DatePicker
            v-model="endDate"
            date-format="M dd, yy"
            :min-date="startDate"
            :max-date="new Date()"
            show-icon
            icon-display="input"
            input-class="text-xs w-[8rem]"
            :pt="{ pcInput: { root: { class: 'text-xs !py-0' } } }"
          />
        </div>

        <!-- Email Search -->
        <div class="flex-1">
          <InputText
            v-model="emailSearchInput"
            placeholder="Search by email..."
            class="w-full text-xs"
            size="small"
          />
        </div>

        <!-- Export Button -->
        <Button
          v-if="!csvLocked"
          label="Export CSV"
          icon="pi pi-download"
          severity="secondary"
          size="small"
          :disabled="totalRecords === 0"
          @click="handleExport"
        />
        <Button
          v-else
          label="Export CSV"
          icon="pi pi-lock"
          severity="secondary"
          size="small"
          disabled
          v-tooltip.top="'CSV exports available on the Scale plan'"
        />
      </div>

      <!-- Table -->
      <div class="min-h-0 flex-1 overflow-hidden">
        <FlowEngagementTable
          :engagements="engagements"
          :flow-id="flowId"
          :loading="isLoading || isFetching"
          :total-records="totalRecords"
          @page="handlePage"
        />
      </div>

      <!-- Pagination -->
      <div
        v-if="totalRecords > pageSize || currentPage > 0"
        class="flex items-center justify-between border-t border-gray-200 px-4 py-2 dark:border-gray-700"
      >
        <span class="text-xs text-gray-500 dark:text-gray-400">
          Showing {{ currentPageStart }}-{{ currentPageEnd }} of {{ totalRecords.toLocaleString() }}
        </span>
        <div class="flex items-center gap-1">
          <Button
            icon="pi pi-chevron-left"
            severity="secondary"
            text
            size="small"
            :disabled="currentPage === 0"
            @click="goToPage(currentPage - 1)"
          />
          <span class="px-2 text-xs text-gray-600 dark:text-gray-400">
            Page {{ currentPage + 1 }} of {{ totalPages }}
          </span>
          <Button
            icon="pi pi-chevron-right"
            severity="secondary"
            text
            size="small"
            :disabled="!hasNextPage"
            @click="goToPage(currentPage + 1)"
          />
        </div>
      </div>
    </div>
  </Drawer>
</template>

<script setup lang="ts">
import { useQuery, keepPreviousData } from '@tanstack/vue-query'
import { useDebounceFn } from '@vueuse/core'
import FlowEngagementTable from './FlowEngagementTable.vue'
import { fetchFlowEngagements } from '../services/flowMetricsService'
import { useFlowEngagementExport } from '../composables/useFlowEngagementExport'

const { isFeatureLocked } = useFeatureGating()
const csvLocked = computed(() => isFeatureLocked('csv_exports'))

const props = defineProps<{
  visible: boolean
  flowId: string
}>()

const emit = defineEmits<{
  'update:visible': [value: boolean]
}>()

const isVisible = computed({
  get: () => props.visible,
  set: (val) => emit('update:visible', val)
})

const teamStore = useTeamStore()
const configStore = useConfigStore()
const rightyStore = useRightyStore()

// Hide Righty floating UI when the engagement drawer is open
watch(isVisible, (visible) => {
  if (visible) {
    rightyStore.hideFloatingUi()
  } else {
    rightyStore.showFloatingUi()
  }
})

onUnmounted(() => {
  rightyStore.showFloatingUi()
})

// Flow name lookup
const flowName = computed(() => {
  const flow = configStore.config?.rightCta?.offerFunnels?.find(
    (f: any) => f.id === props.flowId
  )
  return flow?.name || 'Flow Engagement'
})

// Date range (default last 7 days, independent of metrics tab)
const endDate = ref(new Date())
const startDate = ref((() => {
  const d = new Date()
  d.setDate(d.getDate() - 7)
  return d
})())

// Email search with debounce
const emailSearchInput = ref('')
const emailSearch = ref('')
const debouncedSearch = useDebounceFn((val: string) => {
  emailSearch.value = val
  resetPagination()
}, 500)

watch(emailSearchInput, (val) => {
  debouncedSearch(val)
})

// Pagination
const pageSize = 50
const currentPage = ref(0)
const searchAfterTokens = ref(new Map<number, string>())
const stickyTotal = ref(0)
const stickyTotalAll = ref(0)

function resetPagination() {
  currentPage.value = 0
  searchAfterTokens.value = new Map()
  stickyTotal.value = 0
  stickyTotalAll.value = 0
}

// Reset pagination when date range changes
watch([startDate, endDate], () => {
  resetPagination()
})

const teamPid = computed(() => {
  const pid = teamStore.currentTeam?.pid
  return pid ? String(pid) : undefined
})

// TanStack Query for data fetching
const { data, isLoading, isFetching } = useQuery({
  queryKey: computed(() => [
    'flowEngagements',
    teamPid.value,
    props.flowId,
    startDate.value?.toISOString(),
    endDate.value?.toISOString(),
    emailSearch.value,
    currentPage.value
  ]),
  queryFn: async () => {
    if (!teamPid.value) throw new Error('No team selected')

    const searchAfter = currentPage.value > 0
      ? searchAfterTokens.value.get(currentPage.value - 1)
      : undefined

    return fetchFlowEngagements(teamPid.value, {
      flowId: props.flowId,
      start: startDate.value,
      end: endDate.value,
      email: emailSearch.value || undefined,
      limit: pageSize,
      searchAfter
    })
  },
  enabled: computed(() => props.visible && !!props.flowId && !!teamPid.value),
  placeholderData: keepPreviousData
})

// Store searchAfter token and sticky totals when data arrives
watch(data, (newData) => {
  if (newData?.searchAfter) {
    searchAfterTokens.value.set(currentPage.value, newData.searchAfter)
  }
  if (newData?.total && newData.total > 0) {
    stickyTotal.value = newData.total
  }
  if (newData?.totalAll && newData.totalAll > 0) {
    stickyTotalAll.value = newData.totalAll
  }
})

const engagements = computed(() => data.value?.engagements || [])
const totalRecords = computed(() => stickyTotal.value || data.value?.total || 0)
const totalAllRecords = computed(() => stickyTotalAll.value || data.value?.totalAll || 0)
const totalPages = computed(() => Math.ceil(totalRecords.value / pageSize))
const hasNextPage = computed(() => {
  return !!searchAfterTokens.value.get(currentPage.value) && currentPage.value < totalPages.value - 1
})

const currentPageStart = computed(() => currentPage.value * pageSize + 1)
const currentPageEnd = computed(() => Math.min((currentPage.value + 1) * pageSize, totalRecords.value))

function goToPage(page: number) {
  if (page < 0 || page >= totalPages.value) return
  currentPage.value = page
}

function handlePage(event: { page: number }) {
  goToPage(event.page)
}

// Export — opens server-rendered processing page in new tab
const { startExport } = useFlowEngagementExport()

function handleExport() {
  startExport({
    flowId: props.flowId,
    start: startDate.value,
    end: endDate.value,
    email: emailSearch.value || undefined
  })
}
</script>
